// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Wallet  (route /wallet — the wallet icon in the navbar, before the profile icon)
// The customer's ZenFresh wallet, a CREDITS LEDGER: the current balance, how the wallet works, and the full transaction
// history (newest first, with the balance after each movement and a link to the order it belongs to) with All / Credits /
// Used filters. Credits arrive from ZenFresh only — a refund the customer chose to take as wallet credit, the wallet part
// of a cancelled order, or credit staff grant — and are spent at checkout (the checkout asks first; it is a tick box).
// Deliberately NOT here (nothing supports it): add money / top-up, withdraw / transfer, cashback and "Eco-Credits"
// programmes, expiry dates. Data: GET /api/me/wallet (utils/wallet.ts). Signed-out visitors go to /welcome.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowDownLeft, ArrowLeft, ArrowUpRight, Wallet as WalletIcon } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { formatWhen } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";
import { REASON_LABEL, fetchWallet, type Wallet as WalletData } from "../utils/wallet";

type Filter = "all" | "credit" | "debit";
type Load = { status: "loading" } | { status: "error" } | { status: "ready"; wallet: WalletData };

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "credit", label: "Credits" },
  { id: "debit", label: "Used" },
];

export default function Wallet() {
  useDocumentTitle("Wallet");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [filter, setFilter] = useState<Filter>("all");

  const refresh = () =>
    fetchWallet().then(
      (wallet) => setLoad({ status: "ready", wallet }),
      () => setLoad((current) => (current.status === "ready" ? current : { status: "error" })),
    );

  useEffect(() => {
    if (signedIn) void refresh();
  }, [signedIn]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const wallet = load.status === "ready" ? load.wallet : null;
  const entries = wallet ? wallet.entries.filter((entry) => filter === "all" || entry.kind === filter) : [];
  const chip = (on: boolean) =>
    `inline-flex min-h-11 items-center rounded-full px-5 text-sm font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
      on ? "bg-ocean-800 text-white" : "bg-white text-ocean-900 ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"
    }`;

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Wallet</h1>
          </header>

          <section aria-label="Wallet balance" className="rounded-2xl bg-ocean-800 p-5 text-white shadow-sm">
            <p className="flex items-center gap-2 text-sm font-semibold text-foam-100/90">
              <WalletIcon className="h-4 w-4" aria-hidden="true" /> ZenFresh credits
            </p>
            {load.status === "loading" ? (
              <p role="status" className="mt-2 text-sm">Loading your wallet…</p>
            ) : load.status === "error" ? (
              <div role="alert" className="mt-2">
                <p className="text-sm">We couldn&apos;t load your wallet.</p>
                <button type="button" onClick={() => void refresh()} className="mt-2 min-h-11 rounded-full bg-white px-5 text-sm font-bold text-ocean-900 hover:bg-foam-100">Try again</button>
              </div>
            ) : (
              <>
                <p className="mt-1 font-display text-5xl font-bold tabular-nums" aria-label={`Balance ${formatInr(load.wallet.balance)}`}>{formatInr(load.wallet.balance)}</p>
                <p className="mt-1 text-sm text-foam-100/90">
                  {load.wallet.balance > 0 ? "Ready to use — checkout applies it for you." : "Nothing here yet. Credits from ZenFresh will show up here."}
                </p>
                <Link to="/menu" className="mt-4 inline-flex min-h-11 items-center rounded-full bg-white px-6 text-sm font-bold text-ocean-900 hover:bg-foam-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
                  Shop the catch
                </Link>
              </>
            )}
          </section>

          <section aria-labelledby="how-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
            <h2 id="how-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">How your wallet works</h2>
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-ocean-800/90 dark:text-foam-100/85">
              <li><strong>Credits come from ZenFresh</strong> — a refund you choose to receive as wallet credit when you cancel an order, or a credit we grant you.</li>
              <li><strong>Use it at checkout.</strong> It is applied first; you pay only what is left, and nothing at all if it covers the order. You can untick it.</li>
              <li>If you cancel an order you partly paid from the wallet, that part goes straight back here.</li>
              <li>Wallet credit is for ZenFresh orders only: it can&apos;t be topped up, withdrawn or transferred.</li>
            </ul>
          </section>

          <section aria-labelledby="history-heading" className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <h2 id="history-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Transactions</h2>
              <div role="group" aria-label="Filter transactions" className="flex gap-2">
                {FILTERS.map((f) => (
                  <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)} className={chip(filter === f.id)}>{f.label}</button>
                ))}
              </div>
            </div>

            {wallet && entries.length === 0 && (
              <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
                <p className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{wallet.entries.length === 0 ? "No transactions yet" : "Nothing in this view"}</p>
                <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
                  {wallet.entries.length === 0 ? "When credit is added or used, it will be listed here." : "Try another filter."}
                </p>
              </div>
            )}

            {entries.length > 0 && (
              <ul className="divide-y divide-ocean-900/5 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:divide-white/10 dark:bg-ocean-900 dark:ring-white/10">
                {entries.map((entry) => {
                  const credit = entry.kind === "credit";
                  return (
                    <li key={entry.id} className="flex items-start gap-3 px-4 py-3">
                      <span aria-hidden="true" className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${credit ? "bg-teal-100 text-teal-700" : "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50"}`}>
                        {credit ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-ocean-950 dark:text-foam-50">{REASON_LABEL[entry.reason]}</p>
                        <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">
                          {entry.note}
                          {entry.orderRef && <> · <Link to={`/orders/${entry.orderRef}`} className="font-semibold underline">View order</Link></>}
                        </p>
                        <p className="text-xs text-ocean-800/70 dark:text-foam-100/65">{formatWhen(entry.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-display text-lg font-bold tabular-nums ${credit ? "text-teal-700 dark:text-teal-300" : "text-ocean-950 dark:text-foam-50"}`}>
                          {credit ? "+" : "−"}{formatInr(entry.amount)}
                        </p>
                        <p className="text-xs text-ocean-800/70 dark:text-foam-100/65">Balance {formatInr(entry.balanceAfter)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </PageTransition>
  );
}
